import { createContext } from "react";
import { CUSTOM_FIELD_TYPE } from "~/types/__generated/gql/graphql";

export enum DATA_DISPLAY_CHANGE_TYPE {
	INFO = "info",
	ADDRESSES = "addresses",
	CONTACTS = "contacts",
	SERVICE = "service",
}

export interface DataDisplayList {
	parentFieldName?: string;
	fieldName: string;
	fieldLabel: string;
	fieldType: CUSTOM_FIELD_TYPE;
	sortingIndex: number;
	isCustomField: boolean;
	isPinned: boolean;
	dock?: string;
	width?: number;
}

export interface DataDisplayFormDetails {
	sortingIndex: number;
	fields: DataDisplayFormDetail[];
}

export interface DataDisplayFormDetail {
	parentFieldName?: string;
	fieldName: string;
	fieldLabel: string;
	fieldType: CUSTOM_FIELD_TYPE;
	sortingIndex: number;
	isCustomField: boolean;
	isPinned: boolean;
}

export interface DataDisplayForm {
	info: DataDisplayFormDetails[];
	addresses: DataDisplayFormDetails[];
	contacts: DataDisplayFormDetails[];
	service: DataDisplayFormDetails[];
}

export interface DataDisplayAgent {
	isFormModelOpen: boolean;
	isListModelOpen: boolean;
	list: DataDisplayList[];
	form: DataDisplayForm;
}

export interface DataDisplayClient {
	isFormModelOpen: boolean;
	isListModelOpen: boolean;
	list: DataDisplayList[];
	form: DataDisplayForm;
}

export interface DataDisplayProduct {
	isListModelOpen: boolean;
	list: DataDisplayList[];
}

export interface DataDisplayServiceAgent {
	isListModelOpen: boolean;
	list: DataDisplayList[];
}

export interface DataDisplayServiceClient {
	isListModelOpen: boolean;
	list: DataDisplayList[];
}

export interface DataDisplayState {
	agent: DataDisplayAgent;
	client: DataDisplayClient;
	product: DataDisplayProduct;
	serviceAgent: DataDisplayServiceAgent;
	serviceClient: DataDisplayServiceClient;
}

export const initialState: DataDisplayState = {
	agent: {
		isFormModelOpen: false,
		isListModelOpen: false,
		list: [],
		form: {
			info: [
				{
					sortingIndex: 0,
					fields: [
						{
							fieldName: "name",
							fieldLabel: "Name",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							fieldName: "companyIssuedId",
							fieldLabel: "Company Issued ID",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
			addresses: [
				{
					sortingIndex: 0,
					fields: [
						{
							parentFieldName: "addresses",
							fieldName: "title",
							fieldLabel: "Title",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "country",
							fieldLabel: "Country",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "province",
							fieldLabel: "Province",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 2,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "city",
							fieldLabel: "City",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 3,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
				{
					sortingIndex: 1,
					fields: [
						{
							parentFieldName: "addresses",
							fieldName: "area",
							fieldLabel: "Area",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 4,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "street",
							fieldLabel: "Street",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 5,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "building",
							fieldLabel: "Building",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 6,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
			contacts: [
				{
					sortingIndex: 0,
					fields: [
						{
							parentFieldName: "contacts",
							fieldName: "gender",
							fieldLabel: "Gender",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "email",
							fieldLabel: "Email",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "firstName",
							fieldLabel: "First Name",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 2,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "lastName",
							fieldLabel: "Last Name",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 3,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
				{
					sortingIndex: 1,
					fields: [
						{
							parentFieldName: "contacts",
							fieldName: "telephone",
							fieldLabel: "Telephone",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 4,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "mobile",
							fieldLabel: "Mobile",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 5,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "position",
							fieldLabel: "Position",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 6,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
			service: [
				{
					sortingIndex: 0,
					fields: [
						{
							parentFieldName: "hiddenProductsConfigs",
							fieldName: "categories",
							fieldLabel: "Categories",
							fieldType: CUSTOM_FIELD_TYPE.SELECT_MULTI,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "hiddenProductsConfigs",
							fieldName: "products",
							fieldLabel: "Product",
							fieldType: CUSTOM_FIELD_TYPE.SELECT_MULTI,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "hiddenProductsConfigs",
							fieldName: "types",
							fieldLabel: "Types",
							fieldType: CUSTOM_FIELD_TYPE.SELECT_MULTI,
							sortingIndex: 2,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
		},
	},
	client: {
		isFormModelOpen: false,
		isListModelOpen: false,
		list: [],
		form: {
			info: [
				{
					sortingIndex: 0,
					fields: [
						{
							fieldName: "name",
							fieldLabel: "Product Name",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							fieldName: "companyIssuedId",
							fieldLabel: "Company Issued ID",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
			addresses: [
				{
					sortingIndex: 0,
					fields: [
						{
							parentFieldName: "addresses",
							fieldName: "title",
							fieldLabel: "Title",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "country",
							fieldLabel: "Country",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "province",
							fieldLabel: "Province",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 2,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "city",
							fieldLabel: "City",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 3,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
				{
					sortingIndex: 1,
					fields: [
						{
							parentFieldName: "addresses",
							fieldName: "area",
							fieldLabel: "Area",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 4,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "street",
							fieldLabel: "Street",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 5,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "addresses",
							fieldName: "building",
							fieldLabel: "Building",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 6,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
			contacts: [
				{
					sortingIndex: 0,
					fields: [
						{
							parentFieldName: "contacts",
							fieldName: "gender",
							fieldLabel: "Gender",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "email",
							fieldLabel: "Email",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "firstName",
							fieldLabel: "First Name",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 2,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "lastName",
							fieldLabel: "Last Name",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 3,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
				{
					sortingIndex: 1,
					fields: [
						{
							parentFieldName: "contacts",
							fieldName: "telephone",
							fieldLabel: "Telephone",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 4,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "mobile",
							fieldLabel: "Mobile",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 5,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "contacts",
							fieldName: "position",
							fieldLabel: "Position",
							fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
							sortingIndex: 6,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
			service: [
				{
					sortingIndex: 0,
					fields: [
						{
							parentFieldName: "hiddenProductsConfigs",
							fieldName: "categories",
							fieldLabel: "Categories",
							fieldType: CUSTOM_FIELD_TYPE.SELECT_MULTI,
							sortingIndex: 0,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "hiddenProductsConfigs",
							fieldName: "products",
							fieldLabel: "Product",
							fieldType: CUSTOM_FIELD_TYPE.SELECT_MULTI,
							sortingIndex: 1,
							isCustomField: false,
							isPinned: false,
						},
						{
							parentFieldName: "hiddenProductsConfigs",
							fieldName: "types",
							fieldLabel: "Types",
							fieldType: CUSTOM_FIELD_TYPE.SELECT_MULTI,
							sortingIndex: 2,
							isCustomField: false,
							isPinned: false,
						},
					],
				},
			],
		},
	},
	product: {
		isListModelOpen: false,
		list: [],
	},
	serviceAgent: {
		isListModelOpen: false,
		list: [
			{
				fieldName: "productId",
				fieldLabel: "Product ID",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 0,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "name",
				fieldLabel: "Name",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 1,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "price",
				fieldLabel: "Price",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 2,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "category",
				fieldLabel: "Category",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 3,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "type",
				fieldLabel: "Type",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 4,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "optionGroups",
				fieldLabel: "Option Groups",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 5,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "options",
				fieldLabel: "Options",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 6,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
		],
	},
	serviceClient: {
		isListModelOpen: false,
		list: [
			{
				fieldName: "productId",
				fieldLabel: "Product ID",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 0,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "name",
				fieldLabel: "Name",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 1,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "price",
				fieldLabel: "Price",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 2,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "category",
				fieldLabel: "Category",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 3,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "type",
				fieldLabel: "Type",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 4,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "optionGroups",
				fieldLabel: "Option Groups",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 5,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
			{
				fieldName: "options",
				fieldLabel: "Options",
				fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
				sortingIndex: 6,
				isCustomField: false,
				isPinned: false,
				width: 180,
			},
		],
	},
};

export const productListState: DataDisplayList[] = [
	{
		fieldName: "productId",
		fieldLabel: "Product ID",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 0,
		isCustomField: false,
		isPinned: false,
	},
	{
		fieldName: "name",
		fieldLabel: "Name",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 1,
		isCustomField: false,
		isPinned: false,
	},
	{
		fieldName: "listedPrice",
		fieldLabel: "Price",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 2,
		isCustomField: false,
		isPinned: false,
	},
	{
		fieldName: "category",
		fieldLabel: "Category",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 3,
		isCustomField: false,
		isPinned: false,
	},
	{
		fieldName: "type",
		fieldLabel: "Type",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 4,
		isCustomField: false,
		isPinned: false,
	},
	{
		fieldName: "optionGroups",
		fieldLabel: "Option Groups",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 5,
		isCustomField: false,
		isPinned: false,
	},
	{
		fieldName: "options",
		fieldLabel: "Options",
		fieldType: CUSTOM_FIELD_TYPE.TEXT_SINGLE,
		sortingIndex: 6,
		isCustomField: false,
		isPinned: false,
	},
];

export interface DataDisplayContext extends DataDisplayState {
	handleAgentFormModelOpen: () => void;
	handleAgentFormModelClose: () => void;
	handleAgentListModelOpen: () => void;
	handleAgentListModelClose: () => void;
	handleChangeAgentListSettings: (change: any[]) => void;
	handleChangeAgentFormSettings: (
		type: DATA_DISPLAY_CHANGE_TYPE,
		change: any[],
	) => void;
	handleClientFormModelOpen: () => void;
	handleClientFormModelClose: () => void;
	handleClientListModelOpen: () => void;
	handleClientListModelClose: () => void;
	handleChangeClientListSettings: (change: any[]) => void;
	handleChangeClientFormSettings: (
		type: DATA_DISPLAY_CHANGE_TYPE,
		change: any[],
	) => void;
	handleProductListModelOpen: () => void;
	handleProductListModelClose: () => void;
	handleChangeProductListSettings: (change: any[]) => void;
	handleChangeServiceAgentListSettings: (change: any[]) => void;
	handleChangeServiceClientListSettings: (change: any[]) => void;
}

export const DataDisplayContext = createContext<DataDisplayContext>({
	...initialState,
	handleAgentFormModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handleAgentFormModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleAgentListModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handleAgentListModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleChangeAgentListSettings: function (_change: any[]): void {
		throw new Error("Function not implemented.");
	},
	handleChangeAgentFormSettings: function (
		_type: DATA_DISPLAY_CHANGE_TYPE,
		_change: any[],
	): void {
		throw new Error("Function not implemented.");
	},
	handleClientFormModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handleClientFormModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleClientListModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handleClientListModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleChangeClientListSettings: function (_change: any[]): void {
		throw new Error("Function not implemented.");
	},
	handleChangeClientFormSettings: function (
		_type: DATA_DISPLAY_CHANGE_TYPE,
		_change: any[],
	): void {
		throw new Error("Function not implemented.");
	},
	handleProductListModelOpen: function (): void {
		throw new Error("Function not implemented.");
	},
	handleProductListModelClose: function (): void {
		throw new Error("Function not implemented.");
	},
	handleChangeProductListSettings: function (_change: any[]): void {
		throw new Error("Function not implemented.");
	},
	handleChangeServiceAgentListSettings: function (_change: any[]): void {
		throw new Error("Function not implemented.");
	},
	handleChangeServiceClientListSettings: function (_change: any[]): void {
		throw new Error("Function not implemented.");
	},
});
